import { Box, BoxProps } from '@chakra-ui/react/box';
import { keyframes } from '@emotion/react';
import { memo } from 'react';

import { colorReplacements, formatBuildLog } from '@/components/LogViewer/old/formatBuildLog';
import { LogLevelColor } from '@/pages/BuildDetailsPage/components/StructuredBuildLog/utils/log.utils';
import { StepLogChunkLogLevel } from '@/pages/BuildDetailsPage/types/StepLogResponse';

type LogMessage = { level?: StepLogChunkLogLevel; message: string };

type FormattedLogProps = BoxProps & {
  messages: LogMessage[];
  uniqueId?: string;
};

const animationKeyframes = keyframes`
  0% { background: var(--colors-red-20); }
  to { background: transparent; }
`;

const ROW_GROUPING_LIMIT = 10000;
const COLORED_ELEMENTS_LIMIT = 12000;

const FormattedLog = memo(
  ({ messages, uniqueId, ...rest }: FormattedLogProps) => {
    let lastErrorIndex = -1;
    let colorReplacementCount = 0;
    messages.forEach(({ message, level }, index) => {
      // find the last error index
      if (level === StepLogChunkLogLevel.Error) lastErrorIndex = index;

      // count color replacements for StepLogChunkLogLevel.Normal logs
      colorReplacements.forEach(([regexp]) => {
        if (level === StepLogChunkLogLevel.Normal) {
          colorReplacementCount += (message.match(regexp) || []).length;
        }
      });
    });

    const DISABLE_ROW_GROUPING =
      messages.length > ROW_GROUPING_LIMIT || window.location.search.includes('disable-row-grouping');
    const DISABLE_COLORED_ELEMENTS =
      colorReplacementCount > COLORED_ELEMENTS_LIMIT || window.location.search.includes('disable-colored-elements');

    return (
      <Box
        paddingRight={16}
        paddingLeft={16}
        {...rest}
        fontFamily="mono"
        lineHeight="1.35"
        css={{
          WebkitFontSmoothing: 'subpixel-antialiased',
          a: {
            ':hover': {
              textDecoration: 'underline',
            },
          },
        }}
      >
        {!DISABLE_ROW_GROUPING && !DISABLE_COLORED_ELEMENTS && (
          <>
            {messages.map(({ message, level }, index) => {
              const color = level ? LogLevelColor[level] : LogLevelColor[StepLogChunkLogLevel.Normal];
              const id = uniqueId && index === lastErrorIndex ? uniqueId : undefined;

              return (
                <Box
                  color={color}
                  dangerouslySetInnerHTML={{
                    __html: formatBuildLog(message, { replaceColorsWithHTML: level === StepLogChunkLogLevel.Normal }),
                  }}
                  data-testid={id}
                  id={id}
                  // eslint-disable-next-line react/no-array-index-key
                  key={index}
                  css={{
                    ':target': {
                      animation: `${animationKeyframes} 1 3s cubic-bezier(1,0,.95,.2)`,
                    },
                  }}
                />
              );
            })}
          </>
        )}

        {(DISABLE_ROW_GROUPING || DISABLE_COLORED_ELEMENTS) && (
          <Box
            dangerouslySetInnerHTML={{
              __html: messages
                .map(({ message }) =>
                  formatBuildLog(message, {
                    replaceColorsWithHTML: !DISABLE_COLORED_ELEMENTS,
                    useDivsPerLine: false,
                  }),
                )
                .join('\n'),
            }}
          />
        )}
      </Box>
    );
  },
  (prevProps, nextProps) => prevProps.messages === nextProps.messages,
);
FormattedLog.displayName = 'FormattedLog';

export default FormattedLog;
